import { For, Show, createMemo, createSignal, type Component } from 'solid-js';

import { firstFit, freeCells, rectOf } from '../game/container';
import { moveGold, moveItem, type InventoryItem, type ItemRarity } from '../net/protocol';
import type { GameStore } from '../state/store';
import { useDrag } from './DragContext';
import { Icon } from './Icon';
import { ItemGrid } from './ItemGrid';
import { FloatingTooltip, ItemTooltipBody } from './ItemTooltip';
import { rarityClass } from './stash';
import {
  EMPTY_QUERY,
  VAULT_SORTS,
  VAULT_SORT_LABELS,
  applyQuery,
  isNarrowed,
  toggleFilter,
  vaultFacets,
  type VaultQuery,
} from './vault';

/**
 * The vault, inside the Stash's panel: the bag on the left, and on the right a
 * searchable list of everything stored.
 *
 * ── Why it lives in a workshop panel rather than on a key ────────────────────
 *
 * Because the vault is **a place**. The whole point of the split is that storing
 * something costs a walk home: the server refuses every move touching the vault outside
 * the hub, so a panel reachable from a key would be a panel that says "not in town" for
 * most of a session. Putting it behind the Stash means the affordance and the
 * rule agree — you see the vault exactly where you are allowed to use it.
 *
 * ── Why it is a list and the bag is a grid ───────────────────────────────────
 *
 * It was a grid, and the same grid: 10 × 10 of the bag's cells, with the same footprints
 * and the same overlap test. That made the two containers identical in kind and
 * different only in size, which threw away what the split is for.
 *
 * The bag is a **puzzle** — small, and a greatsword costs eight cells, so deciding what
 * comes home is the pressure that ends a dive. The vault is an **archive**: it holds a
 * thousand items across a hundred hours, and the question asked of it is never "does
 * this fit beside that" but "where is the ring with the fire resistance". Tetris in an
 * archive is filing, not a decision — and a 10 × 10 vault taxed a stored greatsword the
 * same eight cells it cost in the bag, punishing a player twice for keeping a two-hander.
 *
 * So: a capacity of a thousand, and search, filters and sorting instead of geometry. The
 * rules for those live in `vault.ts`, pure and tested without a DOM; this file is the
 * inputs, the rows and the drag plumbing. See
 * `doc/grind/architecture/CONTAINERS-GRID-INVENTORY.md`.
 *
 * ── The honest gap ──────────────────────────────────────────────────────────
 *
 * There is no persistence, so this vault dies with the socket exactly as the bag does —
 * it is a Diablo *stash tab* today, not an account vault. Said here rather than implied,
 * and recorded in `doc/grind/STATUS.md`.
 */
export const VaultSection: Component<{ store: GameStore }> = (props) => {
  const dragging = useDrag();
  const bag = () => props.store.bag();
  const vault = () => props.store.vault();
  const attributes = () => props.store.character()?.attributes.total ?? null;

  /**
   * Which half of the stash is showing.
   *
   * Opens on **Carried**, because the errand that brings a player here is almost always
   * "put this down" — you arrive holding a full bag. Banked is where you go to take
   * something back, which is the rarer trip.
   */
  const [tab, setTab] = createSignal<'carried' | 'banked'>('carried');

  const [query, setQuery] = createSignal<VaultQuery>(EMPTY_QUERY);
  const patch = (over: Partial<VaultQuery>) => setQuery({ ...query(), ...over });

  /**
   * The rows to draw.
   *
   * Memoised, because a thousand items through a filter and a sort on every render of a
   * panel that also has a live drag in it is the one place in this HUD where the work is
   * big enough to matter.
   */
  const shown = createMemo(() => applyQuery(vault().items, query()));
  const facets = createMemo(() => vaultFacets(vault().items));

  /** The item the pointer is resting on, and what to anchor its tooltip to. */
  const [hovered, setHovered] = createSignal<InventoryItem | null>(null);
  const [anchor, setAnchor] = createSignal<HTMLElement | undefined>();

  /**
   * Withdrawing: the item goes to the first bag cell it fits.
   *
   * The landing cell is chosen by the **same row-major first fit the server uses**
   * (`game/container.ts` mirrors `Grid.FirstFit`), so it lands where a pickup would have
   * put it. When the bag has no hole of the right shape there is nothing to send, and the
   * button says so rather than sending a command that can only be refused.
   */
  const withdraw = (item: InventoryItem) => {
    const at = firstFit(bag(), rectOf(item));
    if (!at) return;
    props.store.send(moveItem(item.uuid, 'BAG', at.col, at.row));
  };

  /** Whether a stored item has anywhere to land — drives the row's disabled state. */
  const fitsInBag = (item: InventoryItem) => firstFit(bag(), rectOf(item)) !== null;

  /** Depositing names no cell: the vault appends. The zeroes are what the wire wants. */
  const deposit = (item: InventoryItem) => {
    props.store.send(moveItem(item.uuid, 'VAULT', 0, 0));
  };

  const full = () => vault().capacity > 0 && vault().items.length >= vault().capacity;

  return (
    <section class="station__section vault" aria-label="The stash" data-testid="vault-section">
      <h3 class="station__heading">
        Stash · <span data-testid="vault-used">{vault().items.length}</span> of{' '}
        <span data-testid="vault-capacity">{vault().capacity}</span> stored
      </h3>

      {/* **This note used to say the opposite**, and it was right at the time: there was no
          persistence, so nothing here survived the socket. Characters are saved now — on a
          timer, on disconnect and on shutdown — so the stash is an account vault after all,
          and a warning that it is not would be the one thing on screen that lies. */}
      <p class="sheet__seam" data-testid="vault-note">
        Kept between sessions, on this character. Deposits are refused anywhere but town.
      </p>

      {/*
        **The purse and the vault, side by side, with the move between them.**
        Carried gold is what a death spills; banked gold survives it, and that trade is
        the whole reason the stash holds money at all. A player has to be able to see both
        numbers to make it.

        Whole amounts rather than a slider: the decision is "how much am I willing to lose
        on this dive", and a slider makes that a fiddle rather than a choice.
      */}
      <div class="vault__gold hud-interactive" data-testid="stash-gold">
        <span class="purse">
          <span class="purse__coin" aria-hidden="true" />
          <span data-testid="stash-carried-gold">{props.store.gold()}</span> carried
        </span>
        <span class="vault__arrow" aria-hidden="true">
          ⇄
        </span>
        <span class="purse">
          <span class="purse__coin" aria-hidden="true" />
          <span data-testid="stash-banked-gold">{props.store.stashedGold()}</span> banked
        </span>

        <button
          type="button"
          class="skills__button"
          data-testid="stash-deposit-all"
          disabled={props.store.gold() <= 0}
          onClick={() => props.store.send(moveGold(props.store.gold()))}
        >
          Bank it all
        </button>
        <button
          type="button"
          class="skills__button"
          data-testid="stash-withdraw-all"
          disabled={props.store.stashedGold() <= 0}
          onClick={() => props.store.send(moveGold(-props.store.stashedGold()))}
        >
          Take it all
        </button>
      </div>

      {/*
        ── Two tabs, not two columns ─────────────────────────────────────────

        The bag is a 20 × 8 grid and the stash holds a thousand items; side by side they
        each got half a panel, and the reported symptom was that the stash *felt* far
        smaller than the inventory even though it holds six times as much. A tab gives
        whichever one you are working in the whole width.

        **The cost, stated: you can no longer drag between them**, because they are never
        on screen together. Clicking is the transfer now — a click in Carried deposits, a
        click in Banked withdraws — which was already true and was previously the *second*
        way to do it. Dragging still rearranges the bag within its own tab.
      */}
      <div class="vault__tabs hud-interactive" role="tablist" aria-label="Stash">
        <button
          type="button"
          role="tab"
          class="vault__tab"
          classList={{ 'vault__tab--on': tab() === 'carried' }}
          aria-selected={tab() === 'carried'}
          data-testid="vault-tab-carried"
          onClick={() => setTab('carried')}
        >
          Carried <span class="vault__tab-count">{freeCells(bag())} free</span>
        </button>
        <button
          type="button"
          role="tab"
          class="vault__tab"
          classList={{ 'vault__tab--on': tab() === 'banked' }}
          aria-selected={tab() === 'banked'}
          data-testid="vault-tab-banked"
          onClick={() => setTab('banked')}
        >
          Banked{' '}
          <span class="vault__tab-count">
            {vault().items.length} / {vault().capacity}
          </span>
        </button>
      </div>

      <div class="vault__pane">
        <div class="vault__side" classList={{ 'vault__side--hidden': tab() !== 'carried' }}>
          <h4 class="vault__label">
            Carried · <span data-testid="vault-bag-free">{freeCells(bag())}</span> free
          </h4>
          <ItemGrid
            store={props.store}
            container={bag()}
            testId="vault-bag"
            onActivate={deposit}
            tooltip={(it) => (
              <>
                <ItemTooltipBody item={it} attributes={attributes()} />
                <p class="doll__tip-note">Click to bank it.</p>
              </>
            )}
          />
        </div>

        {/*
          The whole stored side is one drop target, because a list has no places to aim
          at. Hovering it highlights the *list*, not a row: dropping on row four does not
          mean anything different from dropping on row nine, and a per-row highlight would
          promise a precision the container does not have.
        */}
        <div
          class="vault__side vault__side--list"
          classList={{
            'vault__side--over': dragging.hovered()?.kind === 'vault',
            'vault__side--hidden': tab() !== 'banked',
          }}
          data-testid="vault-drop"
          onPointerEnter={() => dragging.hover({ kind: 'vault' })}
          onPointerLeave={() => dragging.hover(null)}
          onPointerUp={() => dragging.drop({ kind: 'vault' })}
        >
          <h4 class="vault__label">
            Stored ·{' '}
            <span data-testid="vault-shown">
              {shown().length === vault().items.length
                ? `${vault().items.length} items`
                : `${shown().length} of ${vault().items.length}`}
            </span>
          </h4>

          <div class="vault__controls hud-interactive">
            <input
              type="search"
              class="vault__search"
              placeholder="Search name, affix, slot…"
              aria-label="Search the vault"
              data-testid="vault-search"
              value={query().text}
              onInput={(e) => patch({ text: e.currentTarget.value })}
            />

            <label class="vault__sort">
              Sort
              <select
                aria-label="Sort the vault by"
                data-testid="vault-sort"
                value={query().sort}
                onChange={(e) => patch({ sort: e.currentTarget.value as VaultQuery['sort'] })}
              >
                <For each={VAULT_SORTS}>
                  {(key) => <option value={key}>{VAULT_SORT_LABELS[key]}</option>}
                </For>
              </select>
            </label>

            {/*
              One button rather than two radio buttons: the direction is a toggle, and the
              arrow is the label. `aria-label` says which way it currently sorts rather
              than what pressing it does, because a screen reader user needs the state.
            */}
            <button
              type="button"
              class="sigil-button vault__dir"
              data-testid="vault-direction"
              aria-label={query().direction === 'ASC' ? 'Ascending' : 'Descending'}
              onClick={() => patch({ direction: query().direction === 'ASC' ? 'DESC' : 'ASC' })}
            >
              {query().direction === 'ASC' ? '↑' : '↓'}
            </button>

            <Show when={isNarrowed(query())}>
              <button
                type="button"
                class="sigil-button vault__clear"
                data-testid="vault-clear"
                onClick={() => setQuery(EMPTY_QUERY)}
              >
                Clear
              </button>
            </Show>
          </div>

          {/*
            Chips are built from what is actually stored (`vaultFacets`), not from the
            full vocabulary: a chip for a slot the player owns nothing in is a control
            that can only ever empty the list.
          */}
          <div class="vault__chips hud-interactive" data-testid="vault-chips">
            <For each={facets().rarities}>
              {(rarity) => (
                <button
                  type="button"
                  class={`vault__chip vault__chip--${rarityClass(rarity)}`}
                  classList={{ 'vault__chip--on': query().rarities.includes(rarity) }}
                  data-testid={`vault-chip-${rarity}`}
                  aria-pressed={query().rarities.includes(rarity)}
                  onClick={() =>
                    patch({ rarities: toggleFilter(query().rarities, rarity) as ItemRarity[] })
                  }
                >
                  {rarity}
                </button>
              )}
            </For>
            <For each={facets().slots}>
              {(slot) => (
                <button
                  type="button"
                  class="vault__chip"
                  classList={{ 'vault__chip--on': query().slots.includes(slot) }}
                  data-testid={`vault-chip-${slot}`}
                  aria-pressed={query().slots.includes(slot)}
                  onClick={() => patch({ slots: toggleFilter(query().slots, slot) })}
                >
                  {slot}
                </button>
              )}
            </For>
            <button
              type="button"
              class="vault__chip"
              classList={{ 'vault__chip--on': query().lockedOnly }}
              data-testid="vault-chip-locked"
              aria-pressed={query().lockedOnly}
              onClick={() => patch({ lockedOnly: !query().lockedOnly })}
            >
              ⚿ Locked
            </button>
          </div>

          <Show
            when={vault().capacity > 0}
            fallback={
              <p class="sheet__seam" data-testid="vault-waiting">
                Awaiting the server's account of what you have stored.
              </p>
            }
          >
            <Show
              when={shown().length > 0}
              fallback={
                <p class="sheet__seam" data-testid="vault-empty">
                  {vault().items.length === 0
                    ? 'Nothing stored. Drag something across, or click it in your bag.'
                    : 'Nothing here matches that.'}
                </p>
              }
            >
              <ul class="vault__list" data-testid="vault-list">
                <For each={shown()}>
                  {(item) => (
                    <li class="vault__row-wrap">
                      <button
                        type="button"
                        class={`vault__row vault__row--${rarityClass(item.rarity)}`}
                        classList={{
                          'vault__row--locked': item.isLocked,
                          'vault__row--dragging': dragging.drag()?.item.uuid === item.uuid,
                          'vault__row--stuck': !fitsInBag(item),
                        }}
                        data-testid={`vault-row-${item.uuid}`}
                        // A row is a drag source as well as a button: the same two
                        // gestures the bag's cells offer, so a player who has learned one
                        // panel has learned both. The grab offset is 0,0 because a list
                        // row has no cells to have grabbed one of.
                        onPointerDown={(event) => {
                          if (event.button !== 0) return;
                          dragging.begin(
                            item,
                            { kind: 'container', container: 'VAULT' },
                            { col: 0, row: 0 },
                            { x: event.clientX, y: event.clientY },
                          );
                        }}
                        /**
                         * **A release on the row the drag started from is a click**, not
                         * a zero-length move — the same reading `ItemGrid.onCellUp` makes,
                         * and it has to be made here too because a row sits *inside* the
                         * list's own drop target and swallows the release. Without it a
                         * mouse click on a row began a drag that nothing ever resolved,
                         * and the trailing `click` was skipped as a pointer click, so
                         * clicking a stored item did nothing at all.
                         *
                         * The item withdrawn is the **dragged** one, never the row
                         * released over: dropping row A onto row B means nothing, and
                         * acting on B would act on an item the player never picked up.
                         */
                        onPointerUp={() => {
                          const d = dragging.drag();
                          if (!d) return;
                          const intent = dragging.drop({ kind: 'vault' });
                          if (intent?.kind === 'nothing' && intent.reason === 'NO_MOVEMENT') {
                            withdraw(d.item);
                          }
                        }}
                        onPointerEnter={(e) => {
                          setHovered(item);
                          setAnchor(e.currentTarget);
                        }}
                        onPointerLeave={() => setHovered(null)}
                        onFocus={(e) => {
                          setHovered(item);
                          setAnchor(e.currentTarget);
                        }}
                        onBlur={() => setHovered(null)}
                        // Right-click withdraws, mirroring the bag's right-click-equips.
                        onContextMenu={(event) => {
                          event.preventDefault();
                          if (dragging.drag()) {
                            dragging.cancel();
                            return;
                          }
                          withdraw(item);
                        }}
                        onClick={(event) => {
                          // A pointer click was already resolved by pointerdown →
                          // pointerup on the drag layer; only the keyboard's synthesised
                          // click (`detail === 0`) should act here, or one gesture would
                          // withdraw twice. Same discriminator as `ItemGrid`.
                          if (event.detail !== 0) return;
                          withdraw(item);
                        }}
                      >
                        <Icon class="vault__glyph" iconId={item.iconId} glyph={item.glyph} />
                        <span class="vault__name">{item.name}</span>
                        <span class="vault__meta">
                          <span class="vault__ilvl">iLvl {item.itemLevel}</span>
                          <span class="vault__slot">{item.slot}</span>
                          {/* The footprint, because it is what a withdrawal will cost the
                              bag — the one piece of geometry a list still has to show. */}
                          <span class="vault__size">
                            {rectOf(item).width}×{rectOf(item).height}
                          </span>
                        </span>
                        <Show when={item.isLocked}>
                          <span class="vault__lock" aria-label="locked">
                            ⚿
                          </span>
                        </Show>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </Show>
        </div>
      </div>

      <Show when={full()}>
        <p class="stash__hint stash__hint--warn" data-testid="vault-full">
          The vault is full. Scrap something, or make room.
        </p>
      </Show>
      <p class="stash__hint">
        {/* **Dragging across is gone with the columns.** The two sides are never on screen
            together now, so a hint that offered it would be advertising a gesture that
            cannot be performed. Dragging still rearranges the bag inside its own tab. */}
        Click an item to send it across. Right-click does the same.
      </p>

      {/* One tooltip for the whole list, for the reason `ItemGrid` keeps one: only one
          row can be hovered, and a portal per row is a thousand effects. */}
      <FloatingTooltip
        anchor={anchor()}
        when={hovered() !== null && dragging.drag() === null}
        testId="vault-tip"
      >
        <Show when={hovered()}>
          {(item) => (
            <>
              <ItemTooltipBody item={item()} attributes={attributes()} />
              <p class="doll__tip-note">
                {fitsInBag(item())
                  ? 'Click or drag to carry it out.'
                  : 'No room in your bag for that shape.'}
              </p>
            </>
          )}
        </Show>
      </FloatingTooltip>
    </section>
  );
};
